import { useContext, useEffect, useMemo, useState } from "preact/hooks";
import { IProgram, IProgramMap } from "../interfaces";
import { IProgramFilter } from "../interfaces/campaign-filter";
import { IProgramsState, ProgramsContext } from "../contexts/program";
import { IStatus } from "../interfaces/status";
import { IDispatch } from "../interfaces/dispatch";
import { IProgramActions, setProgramsAction } from "../actions/program";
import { useSession } from "./session";
import { useStatus } from "./status";
import {
  acceptChallenge,
  checkInStreak,
  fetchAllPrograms,
} from "../network/program";
import { IActionHandler } from "../interfaces/event-actions";
import { getVariableValue } from "../util/state-variable-operation";
import { checkCondition } from "../util/conditional-operation";
import { useAutoRefresh } from "./auto-refresh";

export function usePrograms(): [IProgramsState, IDispatch<IProgramActions>] {
  const context = useContext(ProgramsContext);
  if (context === undefined) {
    throw new Error("usePrograms must be used within a ProgramsProvider");
  }
  return [context.state, context.dispatch];
}

export const useProgramsFilter = (
  filter?: IProgramFilter
): [IProgramMap, IStatus, typeof refresh] => {
  const [programState, programDispatch] = usePrograms();
  const [session] = useSession();
  const [status, setStatus] = useStatus();
  useAutoRefresh([refresh]);

  useEffect(() => {
    if (programState.allProgramsLoaded) {
      setStatus({ state: "LOAD_SUCCESS" });
    } else {
      requestPrograms();
    }
  }, [filter]);

  const requestPrograms = async (skipLoading?: boolean) => {
    try {
      if(!skipLoading) setStatus({ state: "LOADING" });
      let campaignId: string = null;
      if (filter) {
        campaignId = getSingleIdFromFilter("campaignId", filter) as string;
      }
      const programMap: IProgramMap = await fetchAllPrograms(
        session.token,
        campaignId
      );
      programDispatch(
        setProgramsAction({
          programMap,
          allProgramsLoaded: filter ? false : true,
        })
      );
      setStatus({ state: "LOAD_SUCCESS" });
    } catch (e) {
      console.log(e);
      setStatus({ state: "LOAD_ERROR", message: "error loading programs" });
    }
  };

  function refresh() {
    requestPrograms(true);
  }

  const matched: IProgramMap = useMemo(() => {
    if (filter) return filterPrograms(programState, filter);
    else return programState;
  }, [filter, programState.byId]);

  return [matched, status, refresh];
};

export const useSingleProgramFilter = (
  filter: IProgramFilter
): [IProgram | null, IStatus] => {
  if (!filter) throw new Error("Filter Missing");
  const [session] = useSession();
  const [programState, programDispatch] = usePrograms();
  const [status, setStatus] = useStatus();
  useAutoRefresh([requestProgram]);

  useEffect(() => {
    const program = filterSingleProgram(programState, filter);
    if (program) {
      setStatus({ state: "LOAD_SUCCESS" });
    } else {
      requestProgram();
    }
  }, [filter]);

  async function requestProgram() {
    setStatus({ state: "LOADING" });
    try {
      const programMap: IProgramMap = await fetchAllPrograms(
        session.token,
        getSingleIdFromFilter("campaignId", filter)
      );
      programDispatch(setProgramsAction({ programMap }));
      setStatus({ state: "LOAD_SUCCESS" });
    } catch (e) {
      console.log(e);
      setStatus({ state: "LOAD_ERROR", message: "error loading program" });
    }
  }

  const matched: IProgram = useMemo(() => {
    if (filter) return filterSingleProgram(programState, filter);
    else return null;
  }, [filter, programState.byId]);

  let mainStatus: IStatus = status;
  if (matched)
    mainStatus = {
      state: "LOAD_SUCCESS",
    };

  return [matched, mainStatus];
};

export const useProgramActionHandler = (): [IStatus, typeof onAction] => {
  const [status, setStatus] = useStatus();
  const [programState, programDispatch] = usePrograms();
  const [session] = useSession();

  const onAction: IActionHandler = async (action, details) => {
    if (action.store === "PROGRAM") {
      if (action.type === "ACCEPT") {
        setStatus({ state: "LOADING" });
        try {
          const programsMap = await acceptChallenge(
            action.payload.campaignId,
            session.token
          );
          programDispatch(setProgramsAction({ programMap: programsMap }));
          setStatus({ state: "LOAD_SUCCESS" });
        } catch (e) {
          console.log(e);
          setStatus({ state: "LOAD_ERROR", message: "failed to accept" });
        }
      } else if (action.type === "CHECK_IN") {
        setStatus({ state: "LOADING" });
        try {
          const programsMap = await checkInStreak(
            action.payload.campaignId,
            session.token
          );
          programDispatch(setProgramsAction({ programMap: programsMap }));
          setStatus({ state: "LOAD_SUCCESS" });
        } catch (e) {
          console.log(e);
          setStatus({ state: "LOAD_ERROR", message: "failed to accept" });
        }
      } else if (action.type === "SET PROGRAM") {
        programDispatch(action);
      }
    }
  };

  return [status, onAction];
};

const getSingleIdFromFilter = (property: string, filter: IProgramFilter) => {
  const obj = filter[property] as any;
  if (!obj) return null;
  const keys = Object.keys(obj);
  if (keys.length !== 1) return null;
  return keys[0];
};

const didProgramMatch = (
  program: IProgram,
  filter: IProgramFilter
): boolean => {
  if (!filter) return true;
  if (!program) throw new Error("program not passed");
  if (filter.campaignId && !filter.campaignId[program.campaignId]) return false;
  if (filter.type && !filter.type[program.programType]) return false;
  if (
    filter.isAccepted !== undefined &&
    filter.isAccepted !== program.isAccepted
  )
    return false;
  if (
    filter.isAccepted !== undefined &&
    filter.isAccepted !== program.isAccepted
  )
    return false;
  if (
    filter.isCompleted !== undefined &&
    filter.isCompleted !== program.isCompleted
  )
    return false;
  if (filter.isExpired !== undefined && filter.isExpired !== program.isExpired)
    return false;

  if (filter.condition) {
    const condition = filter.condition;
    const leftValue = getVariableValue({ program }, condition.leftOperand);
    const rightValue = getVariableValue({ program }, condition.rightOperand);
    const didMatch = checkCondition(leftValue, rightValue, condition.operator);
    if (!didMatch) return false;
  }
  return true;
};

const filterPrograms = (
  state: IProgramMap,
  filter: IProgramFilter
): IProgramMap => {
  const matched: IProgramMap = { allIds: [], byId: {} };
  if (!filter) return matched;
  for (let i = 0; i < state.allIds.length; i++) {
    const p = state.byId[state.allIds[i]];
    if (didProgramMatch(p, filter)) {
      matched.allIds.push(p.campaignId);
      matched.byId[p.campaignId] = p;
    }
  }
  return matched;
};

const filterSingleProgram = (
  state: IProgramMap,
  filter: IProgramFilter
): IProgram | null => {
  if (filter.campaignId) {
    const keys = Object.keys(filter.campaignId);
    if (keys.length === 1 && state.byId[keys[0]]) {
      return state.byId[keys[0]];
    }
  }
  for (let i = 0; i < state.allIds.length; i++) {
    const p = state.byId[state.allIds[i]];
    if (didProgramMatch(p, filter)) {
      return p;
    }
  }
  return null;
};
